import {
  refreshCartPricing,
  invalidateCartPricing,
  suspendCartPricing,
  unmountCartPricing,
  prepareCartDrawerContent,
} from 'meri-theme';

// The cart section renders money without the currency code and the product page with it.
// Shopify's own sample of both formats tells which wrapper to drop; no amount is computed.
function cartMoney(text, parts) {
  const money = parts.dataset.money;
  const withCurrency = parts.dataset.moneyCurrency;
  const at = money && withCurrency ? withCurrency.indexOf(money) : -1;
  if (!text || at < 0) return text;
  const before = withCurrency.slice(0, at);
  const after = withCurrency.slice(at + money.length);
  return text.startsWith(before) && text.endsWith(after) && text.length > before.length + after.length
    ? text.slice(before.length, text.length - after.length)
    : text;
}

// Fill the cart section's own row template with the product page's Shopify output, so the
// preview row has exactly the geometry of the confirmed row that later replaces it.
function previewRow(
  parts,
  kind,
  item,
  {
    children = false,
    first = false,
    last = false,
    attached = false,
    attachedLast = false,
    hasAttached = false,
    through = false,
  } = {},
) {
  const row = parts.content.querySelector(`[data-cart-preview-line="${kind}"]`)?.cloneNode(true);
  if (!row) return null;
  row.removeAttribute('data-cart-preview-line');
  row.dataset.cartPreview = '';
  if (children) row.classList.add('cart-line--has-children');
  if (first) row.classList.add('cart-line--child-first');
  if (last) row.classList.add('cart-line--child-last');
  // 3.0.4: the classes cart.liquid gives an add-on listed under another add-on, and that add-on.
  if (attached) row.classList.add('cart-line--attached');
  if (attachedLast) row.classList.add('cart-line--attached-last');
  if (hasAttached) row.classList.add('cart-line--has-attached');
  if (through) row.classList.add('cart-line--attached-through');
  const image = row.querySelector('img');
  // 3.0.0 (Owner): an add-on without a picture shows a picture icon in a framed box, as its confirmed row does.
  const placeholder = row.querySelector('template[data-preview-placeholder]');
  if (item.image) image.src = item.image;
  else if (kind === 'child' && placeholder) image?.replaceWith(placeholder.content.cloneNode(true));
  else if (kind === 'child') image?.remove();
  placeholder?.remove();
  for (const link of row.querySelectorAll('.cart-line__image, [data-preview-title]')) link.href = item.url || '#';
  row.querySelector('[data-preview-title]').textContent = item.title;
  const meta = row.querySelector('[data-preview-variant]');
  const lines = [item.variant, item.plan].filter(Boolean);
  if (lines.length) {
    meta.textContent = lines[0];
    if (lines[1]) meta.after(Object.assign(meta.cloneNode(), { textContent: lines[1] }));
  } else meta.remove();
  if (item.properties?.length) {
    const list = document.createElement('dl');
    list.className = 'cart-line__properties';
    for (const [name, value] of item.properties) {
      const property = document.createElement('div');
      property.className = 'cart-line__property';
      property.append(Object.assign(document.createElement('dt'), { textContent: name + ': ' }));
      property.append(Object.assign(document.createElement('dd'), { textContent: value }));
      list.append(property);
    }
    row.querySelector('.cart-line__actions').before(list);
  }
  if (item.discounted) {
    // Shopify names the campaign discount and its amount under the product; that line is
    // only known once the cart is confirmed, so its space is held empty until then.
    const discount = document.createElement('p');
    discount.className = 'cart-line__meta cart-line__discount';
    discount.setAttribute('aria-hidden', 'true');
    discount.textContent = ' ';
    row.querySelector('.cart-line__actions').before(discount);
  }
  if (item.required) row.querySelector('[data-preview-remove]')?.remove();
  const quantity = row.querySelector('[data-preview-quantity]');
  if (quantity) quantity.value = item.quantity;
  const quantityText = row.querySelector('[data-preview-quantity-text]');
  if (quantityText) quantityText.textContent = ' ×' + item.quantity + ' ';
  const quantityLabel = row.querySelector('[data-preview-quantity-label]');
  if (quantityLabel) quantityLabel.textContent = parts.dataset.quantityLabel + ': ' + item.quantity;
  const original = row.querySelector('[data-preview-original]');
  if (item.original) original.textContent = cartMoney(item.original, parts);
  else original.remove();
  const price = row.querySelector('[data-preview-price]');
  price.textContent = cartMoney(item.price, parts);
  if (item.original) price.className = 'price-sale';
  const each = row.querySelector('[data-preview-each]');
  if (each && item.price) each.textContent = parts.dataset.each.replace('__AMOUNT__', item.price);
  else each?.remove();
  return row;
}

if (!customElements.get('meri-cart-drawer')) {
  customElements.define(
    'meri-cart-drawer',
    class extends HTMLElement {
      connectedCallback() {
        if (this.abort) return;
        this.dialog = this.querySelector('dialog');
        this.content = this.querySelector('[data-cart-drawer-content]');
        if (!this.dialog || !this.content || !this.dialog.showModal) return;
        this.abort = new AbortController();
        const { signal } = this.abort;
        document.addEventListener(
          'pointerdown',
          (event) => {
            const link = event.isPrimary && this.cartLink(event);
            if (!link) return;
            this.releasePress();
            this.pressedLink = link;
            link.classList.add('cart-drawer-pressed');
            this.prepare();
          },
          { signal, passive: true },
        );
        for (const type of ['pointerup', 'pointercancel'])
          document.addEventListener(type, () => this.releasePress(), { signal, passive: true });
        document.addEventListener(
          'click',
          (event) => {
            if (!this.cartLink(event)) return;
            if (this.open()) event.preventDefault();
          },
          { signal },
        );
        document.addEventListener(
          'meri:cart:invalidated',
          () => {
            // The pending add refreshes with its own bundled rendering; a separate
            // refresh now would queue an extra render request behind the add.
            if (!this.pendingAdd) invalidateCartPricing(this.content.querySelector('[data-cart-page]'));
          },
          { signal },
        );
        document.addEventListener('meri:product:adding', (event) => this.openForAdd(event.detail), { signal });
        document.addEventListener(
          'meri:product:added',
          (event) => {
            if (!this.pendingAdd) {
              if (this.open()) event.preventDefault();
              return;
            }
            // The drawer already showed this add. Confirm it in the background even if
            // the shopper closed the drawer, and never reopen it on their behalf.
            event.preventDefault();
            this.completeAdd(event.detail?.cart);
          },
          { signal },
        );
        document.addEventListener(
          'meri:product:add-failed',
          (event) => {
            if (!this.pendingAdd) return;
            // Withdraw the preview and show Shopify's actual cart: a failed add can still
            // have added part of the selection.
            this.completeAdd(undefined, event.detail?.message);
          },
          { signal },
        );
        this.querySelector('[data-cart-close]').addEventListener('click', () => this.dialog.close(), { signal });
        this.dialog.addEventListener(
          'close',
          () => {
            if (!this.dialog.open) {
              suspendCartPricing(this.content);
            }
          },
          { signal },
        );
        const warm = () => {
          const initial = this.querySelector('template[data-cart-drawer-initial]')?.content.querySelector(
            '[data-cart-page]',
          );
          if (!initial || Number(initial.dataset.cartItemCount) <= 0 || this.content.querySelector('[data-cart-page]'))
            return;
          const prepare = () => {
            this.warmTask = null;
            if (!document.hidden) this.prepare();
          };
          this.warmTask = globalThis.requestIdleCallback
            ? requestIdleCallback(prepare, { timeout: 1000 })
            : setTimeout(prepare, 0);
        };
        if (document.readyState === 'complete') warm();
        else window.addEventListener('load', warm, { signal, once: true });
      }
      cartLink(event) {
        if (event.defaultPrevented || event.button || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
          return null;
        return event.target.closest('[data-cart-open]');
      }
      releasePress() {
        this.pressedLink?.classList.remove('cart-drawer-pressed');
        this.pressedLink = null;
      }
      cancelWarm() {
        if (this.warmTask == null) return;
        if (globalThis.cancelIdleCallback) cancelIdleCallback(this.warmTask);
        else clearTimeout(this.warmTask);
        this.warmTask = null;
      }
      cartRoot() {
        return this.content?.querySelector('[data-cart-page]');
      }
      prepare() {
        if (!this.isConnected || !this.content) return;
        this.cancelWarm();
        // The pending add will refresh with Shopify's bundled rendering when it completes.
        if (this.pendingAdd) return;
        const root = this.ensureRoot();
        if (root.dataset.cartPricing === 'ready') return;
        // The existing pricing state owns the single pending Promise and queue.
        return refreshCartPricing(root);
      }
      ensureRoot() {
        let root = this.cartRoot();
        if (!root) {
          const initial = this.querySelector('template[data-cart-drawer-initial]')?.content.querySelector(
            '[data-cart-page]',
          );
          if (initial) {
            root = prepareCartDrawerContent(initial.cloneNode(true));
            this.content.append(root);
          }
        }
        if (!root) {
          root = document.createElement('div');
          root.dataset.cartPage = '';
          root.dataset.cartSection = 'cart';
          const message = document.createElement('p');
          message.dataset.cartMessage = '';
          message.className = 'form-message';
          message.setAttribute('role', 'status');
          root.append(message);
          const skeleton = this.querySelector('template[data-cart-drawer-skeleton]');
          if (skeleton) root.append(skeleton.content.cloneNode(true));
          this.content.append(root);
        }
        return root;
      }
      openForAdd(detail) {
        if (!this.isConnected || !this.dialog?.showModal || !this.content || this.pendingAdd) return;
        this.cancelWarm();
        const root = this.ensureRoot();
        // Open first so the retained cart can be measured; the swap below happens in the
        // same task, before the first frame, so nothing flashes.
        if (!this.dialog.open)
          try {
            this.dialog.showModal();
          } catch {
            return;
          }
        const view = this.pendingView(detail?.preview, root);
        if (view) {
          this.content.prepend(view);
          this.content.dataset.cartPending = '';
        }
        this.pendingAdd = detail;
        // A detached form cannot bubble its completion. Only this still-current
        // presentation may finish through the callback retained by its submitter.
        detail.settle = ({ cart, message }) => {
          if (this.isConnected && this.pendingAdd === detail) this.completeAdd(cart, message);
        };
        const context = new URL(location.href);
        for (const key of ['sections', 'section_id']) context.searchParams.delete(key);
        // Ask Shopify to render this cart section in the add response (bundled section rendering).
        detail.renderingContext = {
          sectionId: root.dataset.cartSection,
          sectionsUrl: context.pathname + context.search,
        };
      }
      completeAdd(cart, message) {
        this.settleAdd(refreshCartPricing(this.cartRoot(), cart, { force: true }), () => {
          const region = this.cartRoot()?.querySelector('[data-cart-message]');
          if (region && message) {
            // Recovery can fail too. Keep its retry control while explaining the add failure.
            const retry = region.querySelector('[data-cart-pricing-retry]');
            region.replaceChildren(message, ...(retry ? [' ', retry] : []));
          }
        });
      }
      settleAdd(task, after) {
        const pending = this.pendingAdd;
        const done = () => {
          if (!this.isConnected || this.pendingAdd !== pending) return;
          this.pendingAdd = false;
          this.content.querySelector(':scope > [data-cart-page-preview]')?.remove();
          delete this.content.dataset.cartPending;
          after?.();
        };
        Promise.resolve(task).then(done, done);
      }
      // The retained Shopify cart section with the new lines on top (Shopify lists the newest
      // line first), so the confirmed section replaces it without moving anything; only the
      // subtotal and the checkout button change.
      pendingView(preview, root) {
        const parts = root.querySelector('template[data-cart-preview-parts]');
        if (!parts) return null;
        const wallets = 'shopify-accelerated-checkout-cart, shopify-accelerated-checkout, [data-shopify]';
        const walletHeights = [...root.querySelectorAll(wallets)].map((node) => node.getBoundingClientRect().height);
        const view = document.createElement('div');
        view.className = root.className;
        view.dataset.cartPagePreview = '';
        for (const child of root.children) if (!child.matches('template')) view.append(child.cloneNode(true));
        view.querySelectorAll('[id]').forEach((node) => node.removeAttribute('id'));
        view.querySelectorAll('[data-cart-line]').forEach((node) => node.removeAttribute('data-cart-line'));
        // Live checkout wallets are not duplicated; an empty box keeps their space.
        view.querySelectorAll(wallets).forEach((node, index) => {
          const space = document.createElement('div');
          space.style.blockSize = (walletHeights[index] || 0) + 'px';
          node.replaceWith(space);
        });
        const message = view.querySelector('[data-cart-message]');
        if (message) message.textContent = '';
        let form = view.querySelector('form');
        if (!form) {
          view.querySelector(':scope > .surface')?.remove();
          form = parts.content.querySelector('[data-cart-preview-layout]').cloneNode(true);
          view.append(form);
        }
        if (preview?.title) {
          const addons = preview.addons || [];
          const rows = [previewRow(parts, 'parent', preview, { children: addons.length > 0 })];
          // 3.0.4 (Owner): as cart.liquid lists them, an add-on that goes with the add-on just above it (or with the
          // add-on the row above already goes with) sits under that add-on; the others form the product's row.
          const owners = addons.map(() => -1);
          addons.forEach((addon, index) => {
            if (!addon.with || !index) return;
            const above = owners[index - 1] < 0 ? index - 1 : owners[index - 1];
            if (addons[above].id === addon.with && (!addon.withGroup || addons[above].group === addon.withGroup))
              owners[index] = above;
          });
          const rowStarts = addons.map((_, index) => index).filter((index) => owners[index] < 0);
          addons.forEach((addon, index) => {
            const attached = owners[index] >= 0;
            const hasAttached = !attached && owners[index + 1] === index;
            const through = rowStarts.some((start) => start > index) && (attached || hasAttached);
            const attachedLast = attached && owners[index + 1] !== owners[index];
            rows.push(
              previewRow(parts, 'child', addon, {
                first: index === rowStarts[0],
                last: attached ? attachedLast && !through : !hasAttached && index === rowStarts.at(-1),
                attached,
                attachedLast,
                hasAttached,
                through,
              }),
            );
          });
          form.firstElementChild.prepend(...rows.filter(Boolean));
          const count = view.querySelector('h1 small');
          const added = [preview, ...addons].reduce((total, item) => total + (Number(item.quantity) || 0), 0);
          if (count) count.textContent = '(' + ((Number(root.dataset.cartItemCount) || 0) + added) + ')';
        }
        const total = view.querySelector('.cart-total strong');
        if (total) total.textContent = parts.dataset.calculating;
        view.querySelectorAll('[name="checkout"]').forEach((button) => (button.disabled = true));
        form.inert = true;
        return view;
      }
      open() {
        if (!this.isConnected || !this.dialog) return false;
        if (this.dialog.open) return true;
        // Prepare the retained Shopify markup before the dialog's first frame.
        this.prepare();
        try {
          this.dialog.showModal();
        } catch {
          return false;
        }
        // Reuse the existing serialized Shopify cart/section verification.
        // No second price calculation: totals always come from Shopify.
        return true;
      }
      clear() {
        this.pendingAdd = false;
        unmountCartPricing(this.content);
        this.content.replaceChildren();
      }
      disconnectedCallback() {
        this.cancelWarm();
        this.releasePress();
        this.abort?.abort();
        this.abort = null;
        if (this.dialog?.open) this.dialog.close();
        if (this.content) this.clear();
      }
    },
  );
}
